import { useNavigate } from '@tanstack/react-router'
import { useEffect, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { authPaths, landingPathFor } from '@/features/auth/auth-paths'
import type { SignedInUser, UserRole } from '@/lib/api/contracts'
import { useAuth } from '@/lib/auth/useAuth'

interface RouteGuardProps {
  readonly children: ReactNode
  /**
   * Bu ekranın kabul ettiği roller. Verilmezse yalnız oturum aranır — hangi rolün ne
   * görebileceğine sunucu zaten karar verir, buradaki liste sadece kullanıcıyı boş bir ekrana
   * götürmemek içindir.
   */
  readonly roles?: readonly UserRole[] | undefined
}

/**
 * Bir ekranı oturuma (ve istenirse role) bağlar.
 *
 * **Bu bir güvenlik sınırı değildir.** Asıl koruma sunucudadır: her uç kendi policy'sini uygular ve
 * tenant izolasyonu iki katmanla zorlanır (`docs/spec/03-security.md §2`, ADR-0010). Buradaki iş,
 * yetkisiz birinin göremeyeceği veriyi saklamak değil — o veri zaten gelmez — yetkisiz kullanıcıyı
 * boş kutular ve 403 hatalarıyla dolu bir ekranda bırakmamaktır.
 *
 * Yönlendirme neden `beforeLoad`'da değil bir bileşende: `AuthProvider` açılışta refresh cookie'si
 * ile oturumu geri yüklemeye çalışır ve bu asenkron bir adımdır. Router'ın senkron `beforeLoad`'u
 * o an `user === null` görür ve **oturumu sapasağlam olan** kullanıcıyı giriş ekranına yollardı;
 * kullanıcının yaşadığı şey "her yenilemede rastgele çıkış yaptım" olurdu. Bu yüzden guard,
 * `isRestoring` durulana kadar hiçbir karar vermez.
 */
export function RouteGuard({ children, roles }: RouteGuardProps) {
  const { user, isRestoring } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const destination = isRestoring ? null : redirectFor(user, roles)

  useEffect(() => {
    if (destination !== null) {
      // `replace`: geri tuşu, kullanıcıyı girmesine izin verilmeyen ekrana geri fırlatmasın.
      void navigate({ href: destination, replace: true })
    }
  }, [destination, navigate])

  if (isRestoring) {
    return (
      <output className="text-muted-foreground block p-8 text-sm">{t('common.loading')}</output>
    )
  }

  // Yönlendirme yolda: içeriği bir an için göstermek, yetkisiz kullanıcıya ekranın "açıldığını"
  // düşündürür ve veri çeken hook'ları boşuna çalıştırır.
  return destination === null ? children : null
}

/** Kullanıcının gitmesi gereken yol, ya da bulunduğu yerde kalabiliyorsa `null`. */
function redirectFor(
  user: SignedInUser | null,
  roles: readonly UserRole[] | undefined,
): string | null {
  if (user === null) {
    return authPaths.login
  }

  // Şifre değiştirmesi gereken hesap başka hiçbir ucu kullanamaz — middleware kalanını 403
  // PASSWORD_CHANGE_REQUIRED ile keser. Onu içeride tutmak, açılır açılmaz hata veren bir ekran
  // göstermek olurdu.
  if (user.mustChangePassword) {
    return authPaths.changePassword
  }

  // Aynı gerekçe iki adımlı doğrulama için de geçerli: sunucu `TWO_FACTOR_ENROLMENT_REQUIRED` ile
  // kalan her ucu kesiyor, o yüzden kullanıcıyı içeride tutmak açılır açılmaz hata veren bir ekran
  // göstermek olurdu.
  if (user.twoFactorEnrolmentRequired) {
    return authPaths.twoFactorSetup
  }

  if (roles !== undefined && !roles.includes(user.role)) {
    return '/403'
  }

  return null
}

/**
 * Oturumu olan kullanıcıyı kimlik ekranlarından uzak tutar.
 *
 * Giriş yapmış birine giriş formu göstermek, "zaten giriştesin" demeden çalışan bir ekrandır:
 * kullanıcı şifresini yeniden girer, sonra da neden iki kez girdiğini merak eder.
 */
export function RedirectIfSignedIn({ children }: { readonly children: ReactNode }) {
  const { user, isRestoring } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const destination = !isRestoring && user !== null ? landingPathFor(user) : null

  useEffect(() => {
    if (destination !== null) {
      void navigate({ href: destination, replace: true })
    }
  }, [destination, navigate])

  if (isRestoring) {
    return (
      <output className="text-muted-foreground block p-8 text-sm">{t('common.loading')}</output>
    )
  }

  return destination === null ? children : null
}
